import type { Artifact } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { useSyncExternalStore } from 'react'

const artifactsById = new Map<string, Artifact>()
const listenersById = new Map<string, Set<() => void>>()

function notify(artifactId: string): void {
  listenersById.get(artifactId)?.forEach((listener) => listener())
}

/** Keeps the newest revision; a stale event never overwrites a newer artifact. */
export function putArtifact(artifact: Artifact): Artifact {
  const existing = artifactsById.get(artifact.artifact_id)
  if (existing && existing.revision > artifact.revision) return existing
  if (
    existing &&
    existing.revision === artifact.revision &&
    existing.events.length > artifact.events.length
  ) {
    return existing
  }
  artifactsById.set(artifact.artifact_id, artifact)
  notify(artifact.artifact_id)
  return artifact
}

export function getArtifact(
  artifactId: string | undefined
): Artifact | undefined {
  if (!artifactId) return undefined
  return artifactsById.get(artifactId)
}

export function subscribeArtifact(
  artifactId: string,
  listener: () => void
): () => void {
  const listeners = listenersById.get(artifactId) ?? new Set()
  listeners.add(listener)
  listenersById.set(artifactId, listeners)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) listenersById.delete(artifactId)
  }
}

export function useStoredArtifact(
  artifactId: string | undefined
): Artifact | undefined {
  return useSyncExternalStore(
    (listener) =>
      artifactId ? subscribeArtifact(artifactId, listener) : () => {},
    () => getArtifact(artifactId),
    () => getArtifact(artifactId)
  )
}

export function resetArtifactStore(): void {
  artifactsById.clear()
  listenersById.clear()
}
